import classNames from "classnames";
import type { EngagementLevel } from "../../common/types/Engagement";
import "./Tag.css";
import type { MemberDataRatingScore } from "@albertine/types";
import { roundNumberToDecimals } from "../../common/utils/string.util";

type Props = {
    className?: string;
    children: React.ReactNode;
    disabled?: boolean;
};

type BaseTag = (className?: string) => (props: Props) => React.JSX.Element;

function BaseTag(baseClassName?: string) {
    return function Tag(props: Props) {
        const { className, children, disabled } = props;
        return (
            <span
                className={classNames(
                    "tag",
                    baseClassName,
                    {
                        "label--disabled": disabled,
                    },
                    className,
                )}
            >
                {children}
            </span>
        );
    };
}

function Add(baseTag: BaseTag, className: string) {
    return (newClassName?: string) => baseTag(classNames(className, newClassName));
}

function Curry(baseTag: BaseTag) {
    return (className?: string) => baseTag(className);
}

const Colors = (baseComponent: BaseTag) =>
    Object.assign(baseComponent(), {
        E80: baseComponent("tag--e80"),
        E90: baseComponent("tag--e90"),
        N90: baseComponent("tag--n90"),
        N80: baseComponent("tag--n80"),
        T90: baseComponent("tag--t90"),
        P80: baseComponent("tag--p80"),
        P90: baseComponent("tag--p90"),
        S80: baseComponent("tag--s80"),
        S90: baseComponent("tag--s90"),
        R80: baseComponent("tag--a-ds-deadline-overdue"),
        Y80: baseComponent("tag--deadline-prio1"),
        DeadlineOverdue: baseComponent("tag--a-ds-deadline-overdue"),
        DeadlinePrio1: baseComponent("tag--deadline-prio1"),
        DeadlinePrio2: baseComponent("tag--deadline-prio2"),
        DeadlinePrio3: baseComponent("tag--deadline-prio3"),
        DeadlinePrio4: baseComponent("tag--deadline-prio4"),
    });

const Weights = (baseComponent: BaseTag) =>
    Object.assign(baseComponent(), {
        Normal: Colors(Add(baseComponent, "tag--normal")),
        Bold: Colors(Add(baseComponent, "tag--bold")),
        Light: Colors(Add(baseComponent, "tag--light")),
        ...Colors(Curry(baseComponent)),
    });

const Sizes = (baseComponent: BaseTag) =>
    Object.assign(baseComponent(), {
        Large: Weights(Add(baseComponent, "tag--large")),
        Medium: Weights(Add(baseComponent, "tag--medium")),
        Small: Weights(Add(baseComponent, "tag--small")),
        XSmall: Weights(Add(baseComponent, "tag--xsmall")),
        XXSmall: Weights(Add(baseComponent, "tag--xxsmall")),
    });

const Tag = Sizes(BaseTag);

export default Tag;

export function EngagementTag(props: {
    className?: string;
    engagement: {
        level: EngagementLevel;
        label: string;
    };
}) {
    const { className, engagement } = props;
    switch (engagement.level) {
        case "good":
            return (
                <Tag.XXSmall.P90 className={className}>
                    {engagement.label}
                </Tag.XXSmall.P90>
            );
        case "average":
            return (
                <Tag.XXSmall.Y80 className={className}>
                    {engagement.label}
                </Tag.XXSmall.Y80>
            );
        case "poor":
            return (
                <Tag.XXSmall.E90 className={className}>
                    {engagement.label}
                </Tag.XXSmall.E90>
            );
        default:
            return <Tag.XXSmall className={className}>{engagement.label}</Tag.XXSmall>;
    }
}

export function ScoreTag(props: {
    ratingScore: Pick<MemberDataRatingScore, "average">;
}) {
    const { ratingScore } = props;
    const engagement: { level: EngagementLevel; label: string } = (() => {
        const label = `${roundNumberToDecimals(ratingScore?.average, 1)}/5`;

        if (ratingScore?.average && ratingScore?.average >= 4) {
            return {
                level: "good",
                label,
            };
        }

        if (ratingScore?.average && ratingScore?.average >= 3) {
            return { level: "average", label };
        }

        return { level: "poor", label };
    })();
    return <EngagementTag engagement={engagement} />;
}
